import React, { useEffect, useRef, useState } from 'react';
import nciJson from './data/nci_2030_mid_locations.json';
import cecLowJson from './data/cec_low_cap_2027_locations.json';
import cecBaseJson from './data/cec_base_cap_2027_locations.json';
import cecLow2030Json from './data/cec_low_cap_2030_locations.json';
import cecBase2030Json from './data/cec_base_cap_2030_locations.json';

// Shape that the deckgl-demo iframe expects via UPDATE_DATA postMessage.
// Mirrors the LOCATIONS array baked into the original demo.html.
interface DeckLocation {
  name: string;
  coordinates: [number, number]; // [lng, lat]
  activity: number;              // 0-100
  district?: string;
  demand_kwh?: number;
}

interface LocationsPayload {
  scenario: string;
  source: string;
  station_count: number;
  locations: DeckLocation[];
}

// Camera presets per persona
interface CameraPreset { longitude: number; latitude: number; zoom: number; pitch: number }

const NCI_CAM: CameraPreset  = { longitude: -98,    latitude: 39,   zoom: 3.6, pitch: 35 };
const CEC_CAM: CameraPreset  = { longitude: -119.4,  latitude: 36.7, zoom: 5.5, pitch: 35 };

function resolvePayload(viewAsKey: string, datasetType: string): LocationsPayload {
  if (viewAsKey === 'cec') {
    if (datasetType === 'mhd_charger_low_cap_2027') return cecLowJson as LocationsPayload;
    if (datasetType === 'mhd_charger_base_cap_2027') return cecBaseJson as LocationsPayload;
    if (datasetType === 'mhd_charger_low_cap_2030') return cecLow2030Json as LocationsPayload;
    if (datasetType === 'mhd_charger_base_cap_2030') return cecBase2030Json as LocationsPayload;
  }
  return nciJson as LocationsPayload;
}

function resolveCamera(viewAsKey: string): CameraPreset {
  return viewAsKey === 'cec' ? CEC_CAM : NCI_CAM;
}

function resolveTitle(viewAsKey: string, payload: LocationsPayload): string {
  if (viewAsKey === 'cec') return `Maps — ${payload.scenario}`;
  return 'Maps — NCI 2030 Mid Public Charging Stations';
}

export interface MapsProps {
  viewAsKey: string;
  datasetType: string;
}

export const Maps: React.FC<MapsProps> = ({ viewAsKey, datasetType }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [layer, setLayer] = useState<string>('scatter');

  const payload = resolvePayload(viewAsKey, datasetType);
  const camera  = resolveCamera(viewAsKey);

  // Reset ready state when the iframe src might need re-initialising.
  // The iframe itself stays the same; we just need to re-send data when
  // viewAsKey / datasetType change after it has already fired DECKGL_READY.
  const prevKeyRef = useRef<string>('');
  const currentKey = `${viewAsKey}:${datasetType}`;

  // Listen for DECKGL_READY from the iframe, then send our data + view.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'DECKGL_READY') setReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Push data and camera whenever ready OR when dataset/persona switches.
  useEffect(() => {
    if (!ready) return;
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    prevKeyRef.current = currentKey;
    w.postMessage({ type: 'UPDATE_DATA', payload: payload.locations }, '*');
    w.postMessage(
      {
        type: 'SET_VIEW',
        payload: { ...camera, bearing: 0, transitionDuration: 1200 },
      },
      '*',
    );
    w.postMessage({ type: 'SET_LAYER', payload: layer }, '*');
  }, [ready, currentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendLayer = (mode: string) => {
    setLayer(mode);
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'SET_LAYER', payload: mode },
      '*',
    );
  };

  const statCount = viewAsKey === 'cec'
    ? `${payload.locations.length.toLocaleString()} census tracts · ${payload.station_count.toLocaleString()} total chargers`
    : `${payload.station_count.toLocaleString()} unique stations`;

  return (
    <div style={styles.root}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>{resolveTitle(viewAsKey, payload)}</h1>
          <p style={styles.subtitle}>
            {statCount} &middot; sized and colored by
            log-scaled {viewAsKey === 'cec' ? 'total energy (kWh)' : 'total daily demand'}.
            Source: <code>{payload.source}</code>
          </p>
        </div>
        <div style={styles.toolbar}>
          {LAYERS.map((m) => (
            <button
              key={m.key}
              onClick={() => sendLayer(m.key)}
              style={{
                ...styles.tabBtn,
                ...(layer === m.key ? styles.tabBtnActive : {}),
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </header>
      <div style={styles.frameWrap}>
        <iframe
          ref={iframeRef}
          src={(import.meta.env.DECKGL as string | undefined) ?? '/deckgl'}
          title="NCI 2030 Mid public charging stations"
          style={styles.iframe}
        />
      </div>
    </div>
  );
};

const LAYERS: { key: string; label: string }[] = [
  { key: 'scatter', label: 'Points' },
  { key: 'hex', label: 'Hex heatmap' },
  { key: 'grid', label: 'Grid' },
  { key: 'column', label: '3D columns' },
  { key: 'labels', label: 'Labels' },
];

const styles: Record<string, React.CSSProperties> = {
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: '#0b1020',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #1e293b',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    flexShrink: 0,
  },
  title: { fontSize: '1.1rem', fontWeight: 600, margin: 0 },
  subtitle: { fontSize: '0.8rem', color: '#94a3b8', margin: '0.25rem 0 0' },
  toolbar: { display: 'flex', gap: '0.4rem', flexShrink: 0 },
  tabBtn: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#94a3b8',
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabBtnActive: {
    color: '#63b3ed',
    backgroundColor: 'rgba(99,179,237,0.15)',
    borderColor: 'rgba(99,179,237,0.4)',
  },
  frameWrap: { flex: 1, minHeight: 0, position: 'relative' },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },
};
